import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { InputType } from '@getaccept/fried-tofu';
import PasswordValidator from '@getaccept/lib-shared-new/src/password-validator/PasswordValidator.vue';
import HiddenInput from '@getaccept/lib-shared-new/src/hidden-input/HiddenInput.vue';
import { languages } from '@getaccept/lib-shared-new/src/languages/constants/language';
import { ref, onMounted, computed, defineComponent, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PasswordValidation } from '../../helpers/password-validation';
export default defineComponent({
    name: 'VerifyUserForm',
    components: {
        PasswordValidator,
        HiddenInput,
    },
    props: {
        loading: { type: Boolean, default: false },
        defaultLanguage: { type: String, default: '' },
        defaultFirstName: { type: String, default: '' },
        defaultLastName: { type: String, default: '' },
        isInvitation: { type: Boolean, default: false },
    },
    emits: ['language-change', 'submit'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const unFormattedPhoneNumber = ref('');
        const viewPassword = ref(false);
        const profileInformation = ref({
            firstName: '',
            lastName: '',
            language: '',
            phoneNumber: '',
            password: '',
        });
        const passwordFocused = ref(false);
        const showValidatorError = ref(false);
        const isValidPassword = ref(false);
        const validation = ref({
            firstName: '',
            lastName: '',
            language: '',
            phoneNumber: '',
            password: '',
        });
        watch(() => profileInformation.value.password, () => (validation.value.password = ''));
        watch(() => profileInformation.value.firstName, () => (validation.value.firstName = ''));
        watch(() => profileInformation.value.lastName, () => (validation.value.lastName = ''));
        watch(() => profileInformation.value.phoneNumber, () => (validation.value.phoneNumber = ''));
        const passwordInputType = computed(() => viewPassword.value ? InputType.Text : InputType.Password);
        const languageObject = computed(() => languages.find(language => language.key === profileInformation.value.language));
        const formHasError = computed(() => Object.entries(validation.value)
            .map(entry => entry[1])
            .some(value => !!value));
        const phoneNumberLabel = computed(() => props.isInvitation ? t('phone-number-optional') : t('phone-number'));
        const toggleViewPassword = () => (viewPassword.value = !viewPassword.value);
        const handlePhoneNumberInput = (value) => {
            unFormattedPhoneNumber.value = value;
            const phoneNumber = parsePhoneNumberFromString(unFormattedPhoneNumber.value);
            profileInformation.value.phoneNumber = (phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.number.toString()) || value;
        };
        const handlePasswordFocus = () => (passwordFocused.value = true);
        const handlePasswordBlur = () => (passwordFocused.value = false);
        const handleLanguageChange = (language) => {
            profileInformation.value.language = language.key;
            emit('language-change', language);
        };
        const handleIsValidPassword = (isValid) => (isValidPassword.value = isValid);
        const validatePhoneNumber = (number) => {
            if (props.isInvitation)
                return '';
            const numberRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g;
            if (!numberRegex.test(number)) {
                return t('enter-a-valid-phone-number').toString();
            }
            return '';
        };
        const validateForm = ({ password, firstName, lastName, phoneNumber }) => {
            const passwordError = PasswordValidation.validateNewPassword(password, isValidPassword.value);
            const phoneNumberError = validatePhoneNumber(phoneNumber);
            return {
                password: passwordError,
                firstName: firstName ? '' : t('first-name-is-required').toString(),
                lastName: lastName ? '' : t('last-name-is-required').toString(),
                phoneNumber: phoneNumberError,
                language: '',
            };
        };
        const handleSubmit = () => {
            showValidatorError.value = true;
            validation.value = validateForm(profileInformation.value);
            if (formHasError.value) {
                return;
            }
            emit('submit', profileInformation.value);
        };
        onMounted(() => {
            profileInformation.value.firstName = props.defaultFirstName;
            profileInformation.value.lastName = props.defaultLastName;
            profileInformation.value.language = props.defaultLanguage;
        });
        return {
            unFormattedPhoneNumber,
            viewPassword,
            profileInformation,
            passwordFocused,
            showValidatorError,
            isValidPassword,
            validation,
            validatePhoneNumber,
            onMounted,
            passwordInputType,
            languageObject,
            formHasError,
            toggleViewPassword,
            handlePhoneNumberInput,
            handlePasswordFocus,
            handlePasswordBlur,
            handleLanguageChange,
            handleIsValidPassword,
            validateForm,
            handleSubmit,
            phoneNumberLabel,
            languages,
        };
    },
});
